import { useMemo, useState } from 'react';
import type { RequestSpec } from '@/types';

export function PayloadViewer({ spec }: { spec: RequestSpec }) {
  const [copied, setCopied] = useState(false);

  const json = useMemo(() => {
    if (spec.body === undefined) return '';
    return JSON.stringify(spec.body, null, 2);
  }, [spec.body]);

  const handleCopy = async () => {
    if (!json) return;
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className="lp-surface-dark lp-rounded-2xl lp-overflow-hidden lp-flex lp-flex-col lp-shadow-card"
      style={{ borderRadius: 22 }}
    >
      <div
        className="lp-flex lp-items-center lp-justify-between lp-px-4 lp-py-2.5"
        style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="lp-flex lp-items-center lp-gap-2 lp-text-xs lp-font-mono lp-min-w-0">
          <span style={methodBadgeStyle(spec.method)}>{spec.method}</span>
          <span className="lp-truncate" style={{ color: '#cbd5e1' }}>{spec.path}</span>
        </div>
        {json && (
          <button
            onClick={handleCopy}
            className="lp-text-[11px] lp-font-medium lp-px-2.5 lp-py-1 lp-rounded-md lp-flex lp-items-center lp-gap-1.5 lp-transition-colors"
            style={{ background: 'rgba(255,255,255,0.08)', color: '#e2e8f0' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.16)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
          >
            {copied ? (
              <>
                <svg className="lp-w-3 lp-h-3" style={{ color: '#a99dff' }} fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Copied
              </>
            ) : (
              <>
                <svg className="lp-w-3 lp-h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <rect x="9" y="9" width="11" height="11" rx="2" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 15V5a2 2 0 0 1 2-2h10" />
                </svg>
                Copy
              </>
            )}
          </button>
        )}
      </div>
      <pre
        className="lp-code-pane lp-text-[12.5px] lp-leading-relaxed lp-font-mono lp-p-4 lp-overflow-auto lp-max-h-[440px]"
        style={{ color: '#e2e8f0' }}
      >
        {json || <span style={{ color: '#64748b', fontStyle: 'italic' }}>(no request body)</span>}
      </pre>
    </div>
  );
}

function methodBadgeStyle(method: string): React.CSSProperties {
  const base: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 4,
    letterSpacing: '0.04em',
    border: '1px solid rgba(255,255,255,0.18)',
  };
  switch (method) {
    case 'GET':
      return { ...base, background: 'rgba(16, 185, 129, 0.15)', color: '#6ee7b7', borderColor: 'rgba(110, 231, 183, 0.25)' };
    case 'POST':
      return { ...base, background: 'rgba(98, 86, 255, 0.22)', color: '#cbc4ff', borderColor: 'rgba(203, 196, 255, 0.3)' };
    case 'PATCH':
      return { ...base, background: 'rgba(245, 158, 11, 0.15)', color: '#fcd34d', borderColor: 'rgba(252, 211, 77, 0.25)' };
    case 'PUT':
      return { ...base, background: 'rgba(139, 92, 246, 0.15)', color: '#c4b5fd', borderColor: 'rgba(196, 181, 253, 0.25)' };
    case 'DELETE':
      return { ...base, background: 'rgba(244, 63, 94, 0.15)', color: '#fda4af', borderColor: 'rgba(253, 164, 175, 0.25)' };
    default:
      return { ...base, color: '#cbd5e1' };
  }
}
